import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { TranscriptPanel, VideoPlayer } from "@/components/VideoLibrary";
import { findVideo, videoItems } from "@/lib/video-data";

export const Route = createFileRoute("/blog/videos/$slug")({
  loader: ({ params }) => {
    const video = findVideo(params.slug);
    if (!video) throw notFound();
    return { video };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Video not found — Healic Multispeciality Hospital" }, { name: "robots", content: "noindex" }],
      };
    }
    const v = loaderData.video;
    const title = `${v.title} — Healic Multispeciality Hospital`;
    const url = `https://healic.in/blog/videos/${v.slug}`;
    const absoluteUrl = (value: string) =>
      value.startsWith("http") ? value : `https://healic.in${value}`;
    const videoUrl = v.source.type === "file" ? absoluteUrl(v.source.src) : undefined;
    const thumbnailUrl = v.poster ? absoluteUrl(v.poster) : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: v.description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
        { property: "og:title", content: title },
        { property: "og:description", content: v.description },
        { property: "og:type", content: "video.other" },
        { property: "og:url", content: url },
        ...(thumbnailUrl ? [{ property: "og:image", content: thumbnailUrl }] : []),
        ...(videoUrl
          ? [
              { property: "og:video", content: videoUrl },
              { property: "og:video:type", content: "video/mp4" },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: v.description },
        ...(thumbnailUrl ? [{ name: "twitter:image", content: thumbnailUrl }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: videoUrl && thumbnailUrl
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "VideoObject",
                name: v.title,
                description: v.description,
                thumbnailUrl: [thumbnailUrl],
                contentUrl: videoUrl,
                embedUrl: url,
                ...(v.uploadDate ? { uploadDate: v.uploadDate } : {}),
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Healic Multispeciality Hospital",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://healic.in/favicon.png",
                  },
                },
              }),
            },
          ]
        : [],
    };
  },
  component: VideoPage,
  notFoundComponent: VideoNotFound,
});

function VideoNotFound() {
  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-16 text-center lg:px-8">
        <h1 className="font-display text-2xl font-bold">Video not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-sm font-semibold text-accent">
          Back to the blog
        </Link>
      </div>
    </PageShell>
  );
}

function VideoPage() {
  const { video } = Route.useLoaderData();
  const related = videoItems.filter((v) => v.slug !== video.slug).slice(0, 3);

  return (
    <PageShell>
      <div className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-8 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to blog
          </Link>

          <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-accent">
            {video.kind === "doctor" ? "Doctor video" : "Patient testimonial"} · {video.speaker}
          </p>
          <h1 className="mt-1.5 font-display text-2xl font-bold sm:text-3xl">{video.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{video.description}</p>

          <div className="mt-5 aspect-video w-full overflow-hidden rounded-2xl bg-black">
            <VideoPlayer video={video} />
          </div>

          <TranscriptPanel video={video} />

          {related.length > 0 && (
            <div className="mt-8 pb-12">
              <h2 className="font-display text-lg font-bold">More videos</h2>
              <ul className="mt-3 space-y-2">
                {related.map((v) => (
                  <li key={v.slug}>
                    <Link
                      to="/blog/videos/$slug"
                      params={{ slug: v.slug }}
                      className="block rounded-xl border border-border bg-card p-3 text-sm font-semibold hover:text-accent"
                    >
                      {v.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
